import { useEffect, useState } from 'react';
import { CmsAnnouncementBar } from '../../types/contentstack';
import { WarningIcon } from '../icons';
import { IconButton, IconButtonVariant, IconButtonSize, IconName } from '@hills-design-system/components';
import { useTranslation } from '../../hooks/useTranslation';
import { getAnnouncements } from '../../services/contentstack';

const calculateNextIndices = (
  prev: Record<string, number>,
  bars: CmsAnnouncementBar[]
): Record<string, number> => {
  const next: Record<string, number> = {};
  bars.forEach((bar) => {
    const currentIndex = prev[bar.uid] || 0;
    next[bar.uid] = (currentIndex + 1) % bar.announcements.length;
  });
  return next;
};

export const CmsAnnouncementBanner: React.FC = () => {
  const { locale } = useTranslation();
  const [announcementBars, setAnnouncementBars] = useState<CmsAnnouncementBar[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndices, setCurrentIndices] = useState<Record<string, number>>({});
  const [dismissedBars, setDismissedBars] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        setLoading(true);
        const normalizedLocale = locale.toLowerCase();
        const data = await getAnnouncements<CmsAnnouncementBar>(normalizedLocale);
        
        const validBars = data.filter(
          (bar) => bar.announcements && bar.announcements.length > 0
        );
        
        setAnnouncementBars(validBars);
        
        const initialIndices: Record<string, number> = {};
        validBars.forEach((bar) => {
          initialIndices[bar.uid] = 0;
        });
        setCurrentIndices(initialIndices);
      } catch (err) {
        console.error('[CmsAnnouncementBanner] Failed to fetch announcements:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, [locale]);

  useEffect(() => {
    if (announcementBars.length === 0) return;

    const rotateAnnouncements = () => {
      setCurrentIndices((prev) => calculateNextIndices(prev, announcementBars));
    };

    const interval = setInterval(rotateAnnouncements, 4000);
    return () => clearInterval(interval);
  }, [announcementBars]);

  if (loading || announcementBars.length === 0) {
    return null;
  }

  const hasLink = (announcement: CmsAnnouncementBar['announcements'][0]) => {
    return announcement.link_url && announcement.link_label;
  };

  const handleDismiss = (barUid: string) => {
    setDismissedBars((prev) => new Set(prev).add(barUid));
  };

  const visibleBars = announcementBars.filter((bar) => !dismissedBars.has(bar.uid));

  if (visibleBars.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-75 w-full">
      {visibleBars.map((bar) => {
        const currentIndex = currentIndices[bar.uid] || 0;
        const announcement = bar.announcements[currentIndex];

        return (
          <div
            key={bar.uid}
            className="w-full bg-banner-danger-bg border-b border-banner-danger-border relative z-[2]"
          >
            <div className="max-w-7xl mx-auto px-100 py-75 flex items-center gap-75">
              {/* Left accent bar */}
              <div className="flex-shrink-0 w-1 h-12 bg-banner-danger-icon rounded-full"></div>
              
              {/* Icon */}
              <div className="flex-shrink-0">
                <WarningIcon className="text-banner-danger-icon flex-shrink-0" width={24} height={24} />
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div
                  className="text-detail-90 text-black-800 font-medium leading-detail [&>b]:font-bold"
                  dangerouslySetInnerHTML={{ __html: announcement.message }}
                />
                {hasLink(announcement) && (
                  <>
                    {' '}
                    <a
                      href={announcement.link_url}
                      target={announcement.link_target || '_self'}
                      className="text-primary hover:text-primary-hover font-bold transition-colors duration-200 inline-flex items-center gap-25"
                    >
                      {announcement.link_label}
                      <span className="text-primary hover:text-primary-hover">→</span>
                    </a>
                  </>
                )}
              </div>

              {/* Close button */}
              <IconButton
                icon={IconName.X}
                variant={IconButtonVariant.Tertiary}
                size={IconButtonSize.Small}
                onClick={() => handleDismiss(bar.uid)}
                ariaLabel="Dismiss announcement"
                className="flex-shrink-0"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CmsAnnouncementBanner;
